/**
 * ReviewStepV2 Component
 *
 * Phase 8.1: Step 4 of the v2 assessment wizard
 * Shows read-only category scores calculated from question responses
 */

import { useMemo } from 'react';
import {
  ClipboardCheck,
  Gauge,
  Shield,
  FileText,
  AlertCircle,
  Briefcase,
} from 'lucide-react';
import { RubricLabel } from './RubricSelector';
import type { RubricOption, SectionQuestion, QuestionCategory } from '@guild/shared';
import { getScoreColor, getScoreBgWithBorder } from '../../../lib/score-colors';

interface QuestionResponse {
  score: number | null;
  feedback: string;
}

interface ReviewStepV2Props {
  sopTitle: string;
  sectionTitle: string;
  questions: SectionQuestion[];
  rubricOptions: RubricOption[];
  responses: Record<string, QuestionResponse>;
  managerNotes: string;
  onNotesChange: (notes: string) => void;
}

// Category configuration
const categoryConfig: Record<
  QuestionCategory,
  { label: string; icon: typeof ClipboardCheck; color: string; bgColor: string; weight: number }
> = {
  compliance: {
    label: 'Compliance',
    icon: ClipboardCheck,
    color: 'text-emerald-600',
    bgColor: 'bg-emerald-50 border-emerald-200',
    weight: 0.4,
  },
  efficiency: {
    label: 'Efficiency',
    icon: Gauge,
    color: 'text-blue-600',
    bgColor: 'bg-blue-50 border-blue-200',
    weight: 0.3,
  },
  safety: {
    label: 'Safety',
    icon: Shield,
    color: 'text-orange-600',
    bgColor: 'bg-orange-50 border-orange-200',
    weight: 0.3,
  },
};


export function ReviewStepV2({
  sopTitle,
  sectionTitle,
  questions,
  rubricOptions,
  responses,
  managerNotes,
  onNotesChange,
}: ReviewStepV2Props) {
  // Calculate scores
  const scores = useMemo(() => {
    const categoryScores: Record<QuestionCategory, { total: number; count: number; avg: number }> = {
      compliance: { total: 0, count: 0, avg: 0 },
      efficiency: { total: 0, count: 0, avg: 0 },
      safety: { total: 0, count: 0, avg: 0 },
    };

    let totalAnswered = 0;

    for (const question of questions) {
      const response = responses[question.id];
      if (response?.score !== null && response?.score !== undefined) {
        const category = question.category as QuestionCategory;
        if (categoryScores[category]) {
          categoryScores[category].total += response.score;
          categoryScores[category].count += 1;
        }
        totalAnswered++;
      }
    }

    // Calculate averages
    for (const category of Object.keys(categoryScores) as QuestionCategory[]) {
      const { total, count } = categoryScores[category];
      categoryScores[category].avg = count > 0 ? Math.round(total / count) : 0;
    }

    // Overall score: 40% compliance + 30% efficiency + 30% safety
    const overallScore = Math.round(
      categoryScores.compliance.avg * 0.4 +
        categoryScores.efficiency.avg * 0.3 +
        categoryScores.safety.avg * 0.3
    );

    return {
      categoryScores,
      overallScore,
      totalQuestions: questions.length,
      totalAnswered,
    };
  }, [questions, responses]);

  // Group questions by category for summary
  const questionsByCategory = useMemo(() => {
    const grouped: Record<QuestionCategory, Array<{ question: SectionQuestion; response: QuestionResponse | undefined }>> = {
      compliance: [],
      efficiency: [],
      safety: [],
    };

    for (const question of questions) {
      const category = question.category as QuestionCategory;
      if (grouped[category]) {
        grouped[category].push({
          question,
          response: responses[question.id],
        });
      }
    }

    return grouped;
  }, [questions, responses]);

  const hasUnanswered = scores.totalAnswered < scores.totalQuestions;

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="px-8 py-6 border-b border-gray-100 bg-gradient-to-r from-[#00674a]/5 to-[#c5e8c2]/10">
        <h2 className="text-xl font-bold text-gray-900">Review Assessment</h2>
        <p className="text-sm text-gray-500 mt-1">
          Review scores and add any notes before submitting
        </p>
      </div>

      <div className="p-8 space-y-8">
        {/* Warning if not all answered */}
        {hasUnanswered && (
          <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
            <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">
                Not all questions have been answered
              </p>
              <p className="text-sm text-amber-700 mt-1">
                {scores.totalAnswered} of {scores.totalQuestions} questions answered.
                Unanswered questions will be scored as 0.
              </p>
            </div>
          </div>
        )}

        {/* Assessment Info */}
        <div className="flex items-start gap-4 p-4 bg-gray-50 rounded-xl border border-gray-100">
          <div className="w-12 h-12 rounded-xl bg-[#00674a] flex items-center justify-center flex-shrink-0">
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">{sopTitle}</p>
            <div className="flex items-center gap-2 mt-1">
              <Briefcase className="w-4 h-4 text-[#00674a]" />
              <span className="text-sm text-[#00674a] font-medium">{sectionTitle}</span>
            </div>
          </div>
        </div>

        {/* Overall Score Card */}
        <div className={`rounded-xl p-6 border ${getScoreBgWithBorder(scores.overallScore)}`}>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold text-gray-900">Overall Score</h3>
              <p className="text-sm text-gray-500 mt-1">
                (Compliance × 40%) + (Efficiency × 30%) + (Safety × 30%)
              </p>
            </div>
            <div className="text-right">
              <div className={`text-5xl font-bold ${getScoreColor(scores.overallScore, 'text')}`}>
                {scores.overallScore}
              </div>
              <div className="text-sm text-gray-500 mt-1">out of 100</div>
            </div>
          </div>
        </div>

        {/* Category Scores */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {(Object.keys(categoryConfig) as QuestionCategory[]).map((category) => {
            const config = categoryConfig[category];
            const categoryStats = scores.categoryScores[category];
            const Icon = config.icon;

            return (
              <div
                key={category}
                className={`p-5 rounded-xl border ${config.bgColor}`}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center bg-white/50`}>
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <div>
                    <h4 className={`font-semibold ${config.color}`}>{config.label}</h4>
                    <p className="text-xs text-gray-500">{Math.round(config.weight * 100)}% weight</p>
                  </div>
                </div>
                <div className={`text-3xl font-bold ${config.color}`}>
                  {categoryStats.avg}
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {categoryStats.count} of {questionsByCategory[category].length} questions
                </p>
              </div>
            );
          })}
        </div>

        {/* Question Summary by Category */}
        <div className="space-y-6">
          <h3 className="text-lg font-bold text-gray-900">Question Responses</h3>

          {(Object.keys(categoryConfig) as QuestionCategory[]).map((category) => {
            const config = categoryConfig[category];
            const categoryQuestions = questionsByCategory[category];

            if (categoryQuestions.length === 0) return null;

            return (
              <div key={category} className="border border-gray-200 rounded-xl overflow-hidden">
                <div className={`px-4 py-3 ${config.bgColor} border-b`}>
                  <div className="flex items-center gap-2">
                    <config.icon className={`w-4 h-4 ${config.color}`} />
                    <span className={`font-semibold ${config.color}`}>{config.label}</span>
                    <span className="text-sm text-gray-500">
                      ({categoryQuestions.length} questions)
                    </span>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {categoryQuestions.map(({ question, response }) => (
                    <div key={question.id} className="px-4 py-3 flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 line-clamp-2">
                          <span className="font-medium text-gray-500">Q{question.questionNumber}:</span>{' '}
                          {question.questionText}
                        </p>
                        {response?.feedback && (
                          <p className="text-xs text-gray-500 mt-1 italic">
                            Note: {response.feedback}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        {response?.score !== null && response?.score !== undefined ? (
                          <RubricLabel value={response.score} options={rubricOptions} />
                        ) : (
                          <span className="text-sm text-gray-400 italic">Not scored</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Manager Notes */}
        <div className="space-y-3">
          <label className="block">
            <span className="text-sm font-semibold text-gray-700">Manager Notes</span>
            <span className="text-xs text-gray-400 ml-2">(optional, max 5000 characters)</span>
          </label>
          <textarea
            value={managerNotes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Add any additional notes, observations, or recommendations..."
            rows={4}
            maxLength={5000}
            className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#00674a]/20 focus:border-[#00674a] outline-none transition-all text-gray-900 resize-none bg-white"
          />
          <div className="text-right text-xs text-gray-400">
            {managerNotes.length} / 5000
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReviewStepV2;
